import { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react'
import { authService } from '../services/authService'
import { capabilitiesService, type CapabilitiesResponse } from '../services/capabilitiesService'
import { DEFAULT_FEATURES } from '../config/features'
import type { FeatureKey } from '../config/features'
import { expandPermissionHierarchy, hasPermission as checkPermission, PERMISSIONS } from '../config/permissions'
import { useMfaState } from '../hooks/useMfaState'
import { useSessionManager } from '../hooks/useSessionManager'
import { useTenantState } from '../hooks/useTenantState'
import {
  installSessionExpiryFetchInterceptor,
  subscribeSessionExpired,
} from '../services/sessionExpiry'

export interface AuthUser {
  id?: string
  email?: string
  first_name?: string
  last_name?: string
  tenant_id?: string
  workflow_role?: 'member' | 'approver'
  org_units?: string[]
  is_owner?: boolean
  email_verified?: boolean
  onboarding_completed?: boolean
  force_password_change?: boolean
  // Gate the change-password UI: false ⇒ no local password hash / SSO identity bound; undefined (older backend) ⇒ show the form.
  has_local_password?: boolean
  sso_linked?: boolean
  mfa_verified?: boolean
  effective_features?: Record<FeatureKey, boolean>
  // DESIGN §6A — the "disable multi-tenancy" flag, surfaced as its own
  // session field (NOT merged into effective_features). When true the SPA
  // hides the hub + switcher + create/join.
  single_tenant_mode?: boolean
  plan_features?: Record<FeatureKey, boolean>
  plan_name?: string
  subscription_status?: string
  suspension_reason?: string | null
  trial_started_at?: string | null
  trial_ends_at?: string | null
  trial_days_remaining?: number | null
  trial_extended_days?: number
  is_demo?: boolean
  is_impersonating?: boolean
  original_user_id?: string
  original_user_email?: string
  original_first_name?: string
  original_last_name?: string
  session_timeout_enabled?: boolean
  session_duration_minutes?: number
  [key: string]: unknown
}

export interface OriginalUser {
  id: string
  email: string
  first_name: string
  last_name: string
}

export interface TenantRef {
  id: string
  custom_domain?: string
}

export interface AuthContextType {
  user: AuthUser | null
  loading: boolean
  login: (email: string, password: string) => Promise<unknown>
  switchTenant: (tenant: TenantRef) => Promise<void>
  logout: () => void
  checkAuth: () => Promise<void>
  refreshUser: () => Promise<void>
  // Refresh-token rotation surface. Returns the success body on
  // success, ``null`` on failure (the failure handling — banner /
  // redirect — is encapsulated inside the hook).
  refreshSession: () => Promise<{ ok: boolean; expires_at: string } | null>
  // Banner message rendered after a ``token_reuse_detected`` refresh
  // failure. ``null`` when no banner is active.
  sessionRevokedMessage: string | null
  clearSessionRevokedMessage: () => void
  /**
   * Coarse "user has this permission somewhere" check — true if the
   * permission is granted at tenant scope OR at any org_unit scope
   * (with subtree-inherited descendants pre-expanded server-side).
   *
   * Use this for **page-gate, route-gate, nav-gate, and action-gate
   * visibility** where the backend will per-resource re-check on the
   * actual action. Replaces the legacy ``hasPermission`` (which only
   * saw the tenant frozenset and was the root cause of post-v3
   * "No access" regressions for org_unit-only users — see ADR-0008).
   */
  hasAnyCapability: (permission: string) => boolean
  /**
   * Tenant-scope-only variant of ``hasAnyCapability``. Use for
   * **tenant-administrative actions** (Create Role, Configure Policy,
   * tenant Security settings) that logically apply tenant-wide; a
   * OrgUnit Head should NOT see those even with a org_unit grant.
   */
  hasTenantCapability: (permission: string) => boolean
  /**
   * True iff the permission is granted at the specified org_unit
   * (including via inherited expansion from an ancestor). Future
   * per-org_unit UI affordances (e.g. "upload document to X")
   * should use this.
   */
  hasCapabilityInOrgUnit: (permission: string, orgUnitId: string) => boolean
  /**
   * OrgUnit ids where the permission is granted (does NOT include
   * the tenant scope). Useful for selectors that need to enumerate
   * the user's per-department reach for a given action.
   */
  getCapabilityOrgUnits: (permission: string) => string[]
  impersonate: (userId: string) => Promise<void>
  stopImpersonating: () => Promise<void>
  isDemo: boolean
  /**
   * Single source of truth for the ImpersonationBanner state.
   *
   * `isImpersonating` is `true` iff the backend session response
   * carries `is_impersonating=true` (which itself reflects the JWT
   * cookie's `original_user_id` claim being set). UI components MUST
   * read this flag from `AuthContext` and MUST NOT decode the JWT
   * cookie themselves — there is exactly one decoder, and it lives
   * server-side. See `app/api/v1/auth_session.py::_get_session_impersonation_info`
   * for the resolver and `app/modules/auth/application/impersonation_service.py`
   * for the mint flow.
   */
  isImpersonating: boolean
  /**
   * When `isImpersonating=true`, this carries the impersonated user's
   * id (the value of the JWT `sub` claim while impersonation is
   * active). When `isImpersonating=false`, this is `null`. Useful for
   * components that need to distinguish "viewing as user X" vs
   * "logged in as user X".
   */
  actingAsUserId: string | null
  originalUser: OriginalUser | null
  features: Record<FeatureKey, boolean>
  effectiveFeatures: Record<FeatureKey, boolean>
  isFeatureEnabled: (key: FeatureKey) => boolean
  planName: string
  org_units: string[]
  isAuthenticated: boolean
  hasTenant: boolean
  // DESIGN §6A — true when the user is locked to a single tenant (no hub,
  // no switcher, no create/join). Read by App.jsx + the tenant menu.
  singleTenantMode: boolean
  isOwner: boolean
  isAdmin: boolean
  onboardingCompleted: boolean
  forcePasswordChange: boolean
  subscription_status: string
  suspension_reason: string | null
  trial_started_at: string | null
  trial_ends_at: string | null
  trial_days_remaining: number | null
  trial_extended_days: number
  mfaPending: boolean
  mfaVerified: boolean
  mfaSetupNeeded: boolean
  mfaMethod: string | null
  setMfaPending: (value: boolean) => void
  setMfaSetupNeeded: (value: boolean) => void
  setMfaMethod: (method: string | null) => void
}

const AuthContext = createContext<AuthContextType | null>(null)

interface ExpandedCapabilities {
  tenant: ReadonlySet<string>
  byOrgUnit: ReadonlyMap<string, ReadonlySet<string>>
}

const EMPTY_CAPS: ExpandedCapabilities = {
  tenant: new Set(),
  byOrgUnit: new Map(),
}

/** Apply the v2 permission hierarchy expansion to each bucket. */
function expandCapabilities(raw: CapabilitiesResponse | null): ExpandedCapabilities {
  if (!raw) return EMPTY_CAPS
  const tenant = expandPermissionHierarchy(raw.tenant_capabilities)
  const byOrgUnit = new Map<string, ReadonlySet<string>>()
  for (const [deptId, perms] of Object.entries(raw.org_unit_capabilities)) {
    byOrgUnit.set(deptId, expandPermissionHierarchy(perms))
  }
  return { tenant, byOrgUnit }
}

/** Wildcard-aware match against a single bucket. */
function bucketGrants(bucket: ReadonlySet<string>, perm: string): boolean {
  if (bucket.has(PERMISSIONS.ALL)) return true
  return checkPermission(Array.from(bucket), perm)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Demo impersonation state
  const [isDemo, setIsDemo] = useState(false)
  const [isImpersonating, setIsImpersonating] = useState(false)
  const [originalUser, setOriginalUser] = useState<OriginalUser | null>(null)

  // ── MFA (extracted hook) ──────────────────────────────────────────────────

  const mfa = useMfaState() as any

  // Callback: process session data for demo/impersonation fields
  const handleSessionData = useCallback((data: AuthUser) => {
    setIsDemo(!!data.is_demo)
    setIsImpersonating(!!data.is_impersonating)
    setOriginalUser(data.original_user_id ? {
      id: data.original_user_id,
      email: data.original_user_email ?? '',
      first_name: data.original_first_name ?? '',
      last_name: data.original_last_name ?? '',
    } : null)
  }, [])

  // Callback: handle logout reset for MFA + demo state
  const handleLogout = useCallback(() => {
    mfa.resetMfa()
  }, [mfa.resetMfa])

  // ── Session (extracted hook) ──────────────────────────────────────────────

  const session = useSessionManager({
    onSessionData: handleSessionData,
    onMfaChallenge: mfa.applyMfaFromSession,
    onMfaLogin: mfa.applyMfaFromLogin,
    onLogout: handleLogout,

  }) as any

  useEffect(() => installSessionExpiryFetchInterceptor(), [])

  useEffect(() => {
    return subscribeSessionExpired(session.handleSessionExpired)
  }, [session.handleSessionExpired])

  // ── Tenant (extracted hook) ──────────────────────────────────────────────

  const tenant = useTenantState({ checkAuth: session.checkAuth }) as any

  // ── Capabilities (v3-native, see ADR-0008) ───────────────────────────────
  // Fetched separately from /auth/session so the per-org_unit bucket
  // — which never landed in the legacy frozenset — reaches the
  // frontend gate layer. Refetched on tenant context change (initial
  // load, switch-tenant, logout). Manual ``refreshUser`` ALSO triggers
  // a refetch via the dependency on the user reference so role
  // mutations from inside the same app instance pick up promptly.
  const [capabilitiesRaw, setCapabilitiesRaw] = useState<CapabilitiesResponse | null>(null)
  const u = session.user as AuthUser | null
  const userId = u?.id
  const tenantId = u?.tenant_id
  const activeFeatures = { ...DEFAULT_FEATURES, ...(u?.plan_features ?? {}), ...(u?.effective_features ?? {}) }

  // Capabilities load asynchronously AFTER the session resolves. Route
  // guards (AdminRequired / PermissionRequired) read the capability
  // helpers synchronously, so without a loading signal there is a
  // window where ``session.loading`` is already false but the capability
  // buckets are still empty — the guard would then redirect an admin to
  // /home on a fresh load / direct navigation.
  //
  // We track the tenant id the buckets are *settled* for (set on both
  // success and failure) and DERIVE the loading flag from it. This is
  // synchronously correct across every transition — fresh login,
  // switch-tenant, refreshUser — because the moment ``tenantId`` differs
  // from ``settledTenantId`` the flag is already true, before the effect
  // below even runs. On fetch failure we still mark the tenant settled
  // so guards fail closed (deny) instead of spinning forever.
  const [settledTenantId, setSettledTenantId] = useState<string | null>(null)
  const capabilitiesLoading = Boolean(tenantId) && settledTenantId !== tenantId

  useEffect(() => {
    let cancelled = false
    if (!u || !tenantId) {
      setCapabilitiesRaw(null)
      setSettledTenantId(null)
      return
    }
    capabilitiesService
      .getCapabilities()
      .then((data) => {
        if (!cancelled) {
          setCapabilitiesRaw(data)
          setSettledTenantId(tenantId)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCapabilitiesRaw(null)
          setSettledTenantId(tenantId)
        }
      })
    return () => {
      cancelled = true
    }
  }, [userId, tenantId, u])

  const capabilities = useMemo(
    () => expandCapabilities(capabilitiesRaw),
    [capabilitiesRaw],
  )

  // ── Impersonation ─────────────────────────────────────────────────────────
  const impersonate = async (userId: string) => {
    await authService.impersonate(userId)
    await session.checkAuth()
  }

  const stopImpersonating = async () => {
    await authService.stopImpersonating()
    await session.checkAuth()
  }

  // ── E2E test bridge ──────────────────────────────────────────────────────
  // Expose ``refreshSession`` on ``window`` so the Playwright suite can
  // drive the token-reuse path end-to-end
  // (``tests/features/auth/auth.feature:168``). The FE has no idle
  // refresh trigger — refreshSession only fires on user actions
  // (AcceptInvitation page) — so the E2E needs a programmatic
  // entrypoint to invoke it from inside ``page.evaluate``. Gated on
  // ``import.meta.env.VITE_E2E_HOOKS`` so production bundles never
  // carry the hook — the test stack passes ``VITE_E2E_HOOKS=1`` as a
  // build arg to app/Dockerfile (see ``tests/docker-compose.test.yml``).
  // The previous gate (``import.meta.env.DEV``) didn't work because the
  // E2E app container runs a production nginx build, not the Vite dev
  // server.
  useEffect(() => {
    if (import.meta.env.VITE_E2E_HOOKS !== '1') return
    ;(window as unknown as { __bdRefreshSession?: () => Promise<unknown> }).__bdRefreshSession =
      session.refreshSession
    return () => {
      delete (window as unknown as { __bdRefreshSession?: () => Promise<unknown> })
        .__bdRefreshSession
    }
  }, [session.refreshSession])

  // ── Capability helpers (see ADR-0008) ─────────────────────────────────────

  const hasAnyCapability = useCallback(
    (permission: string): boolean => {
      if (bucketGrants(capabilities.tenant, permission)) return true
      for (const perms of capabilities.byOrgUnit.values()) {
        if (bucketGrants(perms, permission)) return true
      }
      return false
    },
    [capabilities],
  )

  const hasTenantCapability = useCallback(
    (permission: string): boolean => bucketGrants(capabilities.tenant, permission),
    [capabilities],
  )

  const hasCapabilityInOrgUnit = useCallback(
    (permission: string, orgUnitId: string): boolean => {
      const bucket = capabilities.byOrgUnit.get(orgUnitId)
      return bucket ? bucketGrants(bucket, permission) : false
    },
    [capabilities],
  )

  const getCapabilityOrgUnits = useCallback(
    (permission: string): string[] => {
      const out: string[] = []
      for (const [deptId, perms] of capabilities.byOrgUnit.entries()) {
        if (bucketGrants(perms, permission)) out.push(deptId)
      }
      return out
    },
    [capabilities],
  )

  // Tenant-wide admin: holds the wildcard in the tenant bucket. The
  // legacy check inspected ``user.permissions`` which carried the
  // tenant frozenset; the moral equivalent post-ADR-0008 lives in
  // ``capabilities.tenant``.
  const isAdmin = capabilities.tenant.has(PERMISSIONS.ALL)

  // ── Context value (MUST match original useAuth() interface exactly) ──────
  const value: AuthContextType = {
    user: u,
    loading: session.loading || capabilitiesLoading,
    login: session.login,
    switchTenant: tenant.switchTenant,
    logout: session.logout,
    checkAuth: session.checkAuth,
    refreshUser: session.refreshUser,
    refreshSession: session.refreshSession,
    sessionRevokedMessage: session.sessionRevokedMessage ?? null,
    clearSessionRevokedMessage: session.clearSessionRevokedMessage,
    hasAnyCapability,
    hasTenantCapability,
    hasCapabilityInOrgUnit,
    getCapabilityOrgUnits,
    impersonate,
    stopImpersonating,
    isDemo,
    isImpersonating,
    // When impersonating, ``user.id`` IS the impersonated user's id
    // (because the JWT's ``sub`` claim is the target); expose it
    // under a friendlier name so banner code reads intent-fully.
    actingAsUserId: isImpersonating ? (u?.id ?? null) : null,
    originalUser,
    features: activeFeatures,
    effectiveFeatures: activeFeatures,
    isFeatureEnabled: (key: FeatureKey) => activeFeatures[key] ?? false,
    planName: u?.plan_name ?? 'Free',
    org_units: u?.org_units ?? [],
    isAuthenticated: session.isAuthenticated,
    hasTenant: !!u?.tenant_id,
    singleTenantMode: !!u?.single_tenant_mode,
    isOwner: u?.is_owner === true,
    isAdmin,
    onboardingCompleted: !u?.tenant_id || !!u?.onboarding_completed,
    forcePasswordChange: !!u?.force_password_change,
    // Subscription & Trial
    subscription_status: u?.subscription_status ?? 'active',
    suspension_reason: u?.suspension_reason ?? null,
    trial_started_at: u?.trial_started_at ?? null,
    trial_ends_at: u?.trial_ends_at ?? null,
    trial_days_remaining: u?.trial_days_remaining ?? null,
    trial_extended_days: u?.trial_extended_days ?? 0,
    // MFA
    mfaPending: mfa.mfaPending,
    mfaVerified: !!u?.mfa_verified,
    mfaSetupNeeded: mfa.mfaSetupNeeded,
    mfaMethod: mfa.mfaMethod,
    setMfaPending: mfa.setMfaPending,
    setMfaSetupNeeded: mfa.setMfaSetupNeeded,
    setMfaMethod: mfa.setMfaMethod,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
